import React, { useState, useEffect } from 'react';
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';
import { handleGoogleSuccess, getCurrentUser } from '../../services/authService';
import { useNavigate, useLocation } from 'react-router-dom';
import './SignUp.css';
import { useUser } from '../../contexts/UserContext';
import DynamicBackground from '../../components/DynamicBackground';

interface SignUpProps {
  onLogin: () => void;
}

const SignUp: React.FC<SignUpProps> = ({ onLogin }) => {
  const [isLogin, setIsLogin] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const tokenExpired = location.state && location.state.tokenExpired;
  const { setUser, user } = useUser();

  // Helper function to check if token is expired (same logic as ProtectedRoute)
  const isTokenExpired = (idToken: string): boolean => {
    try {
      const base64Url = idToken.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(function (c) {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
          })
          .join('')
      );
      const decoded = JSON.parse(jsonPayload);
      if (!decoded || !decoded.exp) return true;
      const now = Math.floor(Date.now() / 1000);
      return decoded.exp < now;
    } catch (e) {
      return true;
    }
  };

  // Redirect already-authenticated users to /tools or /onboarding
  useEffect(() => {
    const currentUser = getCurrentUser();
    if (currentUser?.idToken && !isTokenExpired(currentUser.idToken)) {
      if (user?.onboarded) {
        navigate('/tools', { replace: true });
      } else if (user && !user.onboarded) {
        navigate('/onboarding', { replace: true });
      }
    }
  }, [navigate, user]);

  const handleGoogleAuth = async (credentialResponse: any) => {
    try {
      setIsLoading(true);
      setError(null);
      
      const user = await handleGoogleSuccess(credentialResponse);
      
      setUser(user);
      onLogin();
      if (!user.onboarded) {
        navigate('/onboarding');
      } else {
        navigate('/tools');
      }
    } catch (error) {
      console.error('Error during authentication:', error);
      const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred. Please try again.';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleError = () => {
    setError('Google login failed. Please try again or use a different account.');
  };

  const clearError = () => {
    setError(null);
  };

  return (
    <DynamicBackground>
      <div className="auth-container">
        {tokenExpired && (
          <div className="expired-token-message">
            Your login session has expired. Please sign in again.
          </div>
        )}
        
        {error && (
          <div className="error-message">
            <span className="error-text">{error}</span>
            <button className="error-close" onClick={clearError}>&times;</button>
          </div>
        )}
        
        <div className="auth-header">
          <h1>{isLogin ? 'Welcome Back' : 'Create Account'}</h1>
          <p>{isLogin ? 'Sign in to continue to your account' : 'Join us to get started'}</p>
        </div>

        <div className="auth-options">
          <div className={`google-button-container ${isLoading ? 'loading' : ''}`}>
            <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID || ''}>
              <GoogleLogin
                onSuccess={handleGoogleAuth}
                onError={handleGoogleError}
                useOneTap
                theme="filled_blue"
              />
            </GoogleOAuthProvider>
          </div>

          {isLoading && (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>Setting up your account...</p>
            </div>
          )}

          <div className="auth-divider">
            <span>or</span>
          </div>

          <div className="auth-switch">
            {isLogin ? (
              <p>
                Don't have an account?{' '}
                <button 
                  className="auth-link-button"
                  onClick={() => {
                    clearError();
                    setIsLogin(false);
                  }}
                  disabled={isLoading}
                >
                  Sign up
                </button>
              </p>
            ) : (
              <p>
                Already have an account?{' '}
                <button 
                  className="auth-link-button"
                  onClick={() => {
                    clearError();
                    setIsLogin(true);
                  }}
                  disabled={isLoading}
                >
                  Sign in
                </button>
              </p>
            )}
          </div>
        </div>
      </div>
    </DynamicBackground>
  );
};

export default SignUp; 